import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, Drawer, IconButton, InputAdornment, MenuItem, Pagination, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  MdAccountBalance, MdAccountBalanceWallet, MdClose, MdHistory, MdPayments, MdReceiptLong, MdRefresh, MdSearch, MdStore, MdTrendingUp, MdWarning,
} from 'react-icons/md';
import { FiFilter } from 'react-icons/fi';
import { getData, postData } from '../../Webservice/Webservice';

const FONT = 'Mulish, sans-serif';
const LIMIT = 15;

const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const dt = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const methodLabel = (m) => (m ? String(m).replace(/_/g, ' ') : '—');
const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' }, '&.Mui-disabled': { background: '#f1f5f9', color: '#94a3b8', boxShadow: 'none' } };
const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
const cell = { fontFamily: FONT, fontSize: 13.5 };
const iconBtn = (c) => ({ color: c, background: `${c}14`, borderRadius: '8px', '&:hover': { background: `${c}26` } });
const dialogPaper = { sx: { borderRadius: '18px', background: '#f8fafc' } };
const MORE = { minPending: '', maxPending: '', minFinalInvoice: '', maxFinalInvoice: '' };

const Kpi = ({ title, value, hint, icon: Icon, from, to, active, onClick }) => (
  <Box onClick={onClick} sx={{ flex: 1, minWidth: 200, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, display: 'flex', alignItems: 'center', gap: 1.5, cursor: onClick ? 'pointer' : 'default', outline: active ? '3px solid #0f172a' : 'none', outlineOffset: 2 }}>
    <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '12px', p: 1.1, display: 'flex' }}><Icon size={22} /></Box>
    <Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 21, fontWeight: 800, lineHeight: 1.15 }}>{value}</Typography>
      {hint && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, opacity: 0.85 }}>{hint}</Typography>}
    </Box>
  </Box>
);

const DialogHeader = ({ title, sub, accent, onClose, disabled }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, borderBottom: `3px solid ${accent}`, background: '#fff' }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 19 }}>{title}</Typography>
      {sub && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{sub}</Typography>}
    </Box>
    <IconButton onClick={onClose} disabled={disabled} size="small"><MdClose size={20} /></IconButton>
  </Box>
);

const Line = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.9, borderBottom: '1px dashed #e2e8f0' }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{label}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Box>
);

const PayChip = ({ pending, paid }) => {
  const settled = pending <= 0.005;
  const label = settled ? 'Paid' : paid > 0 ? 'Part paid' : 'Unpaid';
  const c = settled ? ['#dcfce7', '#166534'] : paid > 0 ? ['#dbeafe', '#1e40af'] : ['#fef3c7', '#92400e'];
  return <Chip size="small" label={label} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: c[0], color: c[1] }} />;
};

function SupplierPayout() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ suppliers: 0, totalPending: 0, suppliersWithPending: 0, totalInvoiced: 0, totalReturns: 0, totalPaid: 0, missingBank: 0, advance: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [pending, setPending] = useState('');
  const [bank, setBank] = useState('');
  const [status, setStatus] = useState('');
  const [more, setMore] = useState(MORE);
  const [drawer, setDrawer] = useState(false);
  const [draft, setDraft] = useState(MORE);
  const seq = useRef(0);

  const [supplier, setSupplier] = useState(null);

  // pay
  const [payOpen, setPayOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [form, setForm] = useState({ receivingId: '', amount: '', paymentMethod: 'bank_transfer', referenceNumber: '', note: '' });
  const [errors, setErrors] = useState({});
  const [payError, setPayError] = useState('');
  const [paying, setPaying] = useState(false);

  // history / invoices / bank
  const [histOpen, setHistOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: LIMIT, search: debounced, pending, bank, status, ...more };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData('supplier/payment/all', params, authToken(), false);
      if (mine !== seq.current) return;
      setRows(res?.data || []);
      setMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
      if (res?.stats) setStats(res.stats);
    } catch {
      if (mine === seq.current) setError('Could not load supplier payouts.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, debounced, pending, bank, status, more]);
  useEffect(() => { load(); }, [load]);

  const activeMore = Object.values(more).filter(Boolean).length;
  const dirty = !!search || !!pending || !!bank || !!status || activeMore > 0;

  const loadInvoices = async (s) => {
    setInvLoading(true);
    try {
      const res = await getData(`supplier/receiving/${s.supplierId}`, undefined, authToken(), false);
      setInvoices(res?.data || []);
    } catch { setInvoices([]); } finally { setInvLoading(false); }
  };

  // ---------- pay ----------
  const openPay = (s, invoice) => {
    setSupplier(s);
    setForm({
      receivingId: invoice?._id || '',
      amount: invoice ? String(invoice.pendingAmount) : '',
      paymentMethod: s.bankDetails?.linked ? 'bank_transfer' : (s.bankDetails?.preferredMode === 'UPI' ? 'upi' : 'bank_transfer'),
      referenceNumber: '',
      note: '',
    });
    setErrors({});
    setPayError('');
    setInvOpen(false);
    setPayOpen(true);
    loadInvoices(s);
  };
  const chosenInvoice = invoices.find((i) => i._id === form.receivingId);
  const limitAmount = chosenInvoice ? chosenInvoice.pendingAmount : Math.max(0, supplier?.totalPendingAmount || 0);

  const pickInvoice = (id) => {
    const inv = invoices.find((i) => i._id === id);
    setForm((f) => ({ ...f, receivingId: id, amount: inv ? String(inv.pendingAmount) : f.amount }));
  };

  const submitPay = async () => {
    setPayError('');
    const e = {};
    const amt = Number(form.amount);
    if (!form.amount || !Number.isFinite(amt) || amt <= 0) e.amount = 'Enter an amount above zero';
    else if (amt - limitAmount > 0.005) e.amount = `At most ${inr(limitAmount)} is owed here`;
    if (['bank_transfer', 'upi', 'cheque'].includes(form.paymentMethod) && !form.referenceNumber.trim()) e.referenceNumber = form.paymentMethod === 'cheque' ? 'Enter the cheque number' : 'Enter the transaction reference';
    setErrors(e);
    if (Object.keys(e).length) return;
    setPaying(true);
    try {
      await postData('supplier/payment-add', {
        supplierId: supplier.supplierId,
        receivingId: form.receivingId || undefined,
        amount: amt,
        paymentMethod: form.paymentMethod,
        referenceNumber: form.referenceNumber,
        note: form.note,
      }, authToken(), false);
      setPayOpen(false);
      setNotice(`Recorded ${inr(amt)} paid to ${supplier.supplierName}.`);
      load();
    } catch (err) {
      setPayError(err?.message || 'Could not record the payment.');
    } finally {
      setPaying(false);
    }
  };

  // ---------- history ----------
  const openHistory = async (s) => {
    setSupplier(s);
    setHistOpen(true);
    setHistory(null);
    setHistLoading(true);
    try {
      setHistory(await getData(`supplier/payment-list/${s.supplierId}`, undefined, authToken(), false));
    } catch { setHistory({ data: [] }); } finally { setHistLoading(false); }
  };

  const openInvoices = (s) => { setSupplier(s); setInvOpen(true); loadInvoices(s); };
  const openBank = (s) => { setSupplier(s); setBankOpen(true); };

  const b = supplier?.bankDetails;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Supplier payouts</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>What you owe each supplier on credit, and the payments you have made.</Typography>
        </Box>
        <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={loading} sx={{ background: '#f1f5f9', borderRadius: '10px' }}><MdRefresh size={20} /></IconButton></span></Tooltip>
      </Box>

      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi title="Credit pending" value={inr(stats.totalPending)} hint={`${stats.suppliersWithPending} supplier(s) to pay`} icon={MdAccountBalanceWallet} from="#f59e0b" to="#b45309" active={pending === 'yes'} onClick={() => { setPending(pending === 'yes' ? '' : 'yes'); setPage(1); }} />
        <Kpi title="Invoiced (after returns)" value={inr(stats.totalInvoiced)} hint={`Returned ${inr(stats.totalReturns)}`} icon={MdTrendingUp} from="#6366f1" to="#4338ca" />
        <Kpi title="Paid so far" value={inr(stats.totalPaid)} hint={stats.advance > 0 ? `${inr(stats.advance)} paid in advance` : undefined} icon={MdPayments} from="#22c55e" to="#15803d" />
        <Kpi title="Suppliers" value={stats.suppliers} icon={MdStore} from="#0ea5e9" to="#0369a1" />
        <Kpi title="Owed but no bank details" value={stats.missingBank} icon={MdWarning} from="#e11d48" to="#9f1239" active={pending === 'yes' && bank === 'missing'} onClick={() => { const on = pending === 'yes' && bank === 'missing'; setPending(on ? '' : 'yes'); setBank(on ? '' : 'missing'); setPage(1); }} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search supplier, ID, phone or email…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Balance" value={pending} onChange={(e) => { setPending(e.target.value); setPage(1); }} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="yes">Money owed</MenuItem><MenuItem value="no">Settled</MenuItem>
        </TextField>
        <TextField select size="small" label="Bank details" value={bank} onChange={(e) => { setBank(e.target.value); setPage(1); }} sx={{ minWidth: 150 }}>
          <MenuItem value="">Any</MenuItem><MenuItem value="linked">Linked</MenuItem><MenuItem value="missing">Missing</MenuItem>
        </TextField>
        <TextField select size="small" label="Supplier status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 150 }}>
          <MenuItem value="">Any</MenuItem><MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
        <Button startIcon={<FiFilter />} onClick={() => { setDraft(more); setDrawer(true); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '10px', px: 2 }}>
          Amounts{activeMore ? ` (${activeMore})` : ''}
        </Button>
        {dirty && <Button onClick={() => { setSearch(''); setPending(''); setBank(''); setStatus(''); setMore(MORE); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Supplier</TableCell>
                <TableCell sx={head} align="right">Invoiced</TableCell>
                <TableCell sx={head} align="right">Returns</TableCell>
                <TableCell sx={head} align="right">Paid</TableCell>
                <TableCell sx={head} align="right">Credit pending</TableCell>
                <TableCell sx={head}>Last payment</TableCell>
                <TableCell sx={head} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(6)].map((_, i) => <TableRow key={i}>{[...Array(7)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>No suppliers match these filters.</TableCell></TableRow>}
              {!loading && rows.map((s) => {
                const owed = s.totalPendingAmount > 0.005;
                return (
                  <TableRow key={s.supplierId} hover>
                    <TableCell sx={cell}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Avatar sx={{ width: 34, height: 34, bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 800, fontSize: 14 }}>{(String(s.supplierName || 'S')[0] || 'S').toUpperCase()}</Avatar>
                        <Box>
                          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }}>{s.supplierName || 'Supplier'}</Typography>
                          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{[s.supplierCode, s.phone].filter(Boolean).join(' · ')}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={cell} align="right">{inr(s.totalFinalInvoiceAmount)}</TableCell>
                    <TableCell sx={{ ...cell, color: s.totalReturnAmount ? '#dc2626' : '#94a3b8' }} align="right">{inr(s.totalReturnAmount)}</TableCell>
                    <TableCell sx={{ ...cell, color: '#166534', fontWeight: 700 }} align="right">{inr(s.totalPaidAmount)}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14, color: owed ? '#b45309' : s.totalPendingAmount < -0.005 ? '#1d4ed8' : '#94a3b8' }}>
                        {s.totalPendingAmount < -0.005 ? `${inr(-s.totalPendingAmount)} advance` : inr(s.totalPendingAmount)}
                      </Typography>
                      {s.unpaidInvoiceCount > 0 && (
                        <Typography onClick={() => openInvoices(s)} sx={{ fontFamily: FONT, fontSize: 12, color: '#4f46e5', cursor: 'pointer', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}>{s.unpaidInvoiceCount} unpaid invoice{s.unpaidInvoiceCount === 1 ? '' : 's'}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={cell}>{s.lastPaymentDate ? fmtDate(s.lastPaymentDate) : <span style={{ color: '#94a3b8' }}>Never</span>}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button size="small" disabled={!owed} onClick={() => openPay(s)} sx={{ ...yellowBtn, px: 1.75, py: 0.5, fontSize: 12.5 }}>Pay</Button>{' '}
                      <Tooltip title="Payment history"><IconButton size="small" onClick={() => openHistory(s)} sx={iconBtn('#4f46e5')}><MdHistory size={17} /></IconButton></Tooltip>{' '}
                      <Tooltip title={s.bankDetails?.linked ? `Bank details · ${s.bankDetails.accountMasked}` : 'No bank details on file'}><IconButton size="small" onClick={() => openBank(s)} sx={iconBtn(s.bankDetails?.linked ? '#16a34a' : '#dc2626')}><MdAccountBalance size={17} /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} supplier{meta.total === 1 ? '' : 's'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" />
        </Box>
      </Box>

      {/* amounts drawer */}
      <Drawer anchor="right" open={drawer} onClose={() => setDrawer(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 340 }, p: 3 } }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 2 }}>Filter by amount</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[['minPending', 'Pending — at least (₹)'], ['maxPending', 'Pending — at most (₹)'], ['minFinalInvoice', 'Invoiced — at least (₹)'], ['maxFinalInvoice', 'Invoiced — at most (₹)']].map(([k, l]) => (
            <TextField key={k} size="small" type="number" label={l} value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
          <Button fullWidth onClick={() => { setDraft(MORE); setMore(MORE); setPage(1); setDrawer(false); }} sx={{ textTransform: 'none', fontWeight: 700 }}>Reset</Button>
          <Button fullWidth variant="contained" onClick={() => { setMore(draft); setPage(1); setDrawer(false); }} sx={yellowBtn}>Apply</Button>
        </Box>
      </Drawer>

      {/* pay */}
      <Dialog open={payOpen} onClose={() => !paying && setPayOpen(false)} maxWidth="sm" fullWidth scroll="paper" PaperProps={dialogPaper}>
        <DialogHeader title="Pay supplier" sub={supplier?.supplierName} accent="rgb(255 209 2)" onClose={() => setPayOpen(false)} disabled={paying} />
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ p: 2.25, borderRadius: '14px', background: '#fffbeb', border: '1px solid #fde68a' }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: '#92400e' }}>CREDIT PENDING</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 30, fontWeight: 800, color: '#78350f', lineHeight: 1.15 }}>{inr(supplier?.totalPendingAmount)}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#a16207' }}>Invoiced {inr(supplier?.totalFinalInvoiceAmount)} · paid {inr(supplier?.totalPaidAmount)}</Typography>
          </Box>

          {form.paymentMethod === 'bank_transfer' && (
            supplier?.bankDetails?.linked ? (
              <Box sx={{ p: 2, borderRadius: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 800, color: '#166534', mb: 0.5 }}>TRANSFER TO</Typography>
                <Line label="Account holder" value={supplier.bankDetails.accountHolder} />
                <Line label="Account" value={supplier.bankDetails.accountMasked} />
                <Line label="IFSC" value={supplier.bankDetails.ifsc} />
              </Box>
            ) : (
              <Alert severity="warning">No bank details are on file for this supplier. Add them in Supplier management, or record a different payment method.</Alert>
            )
          )}

          <TextField select size="small" label="Pay against" value={form.receivingId} onChange={(e) => pickInvoice(e.target.value)} disabled={invLoading}
            helperText={form.receivingId ? undefined : 'No invoice chosen: the payment reduces the supplier’s overall balance'}
            InputProps={{ endAdornment: invLoading ? <CircularProgress size={16} sx={{ mr: 2 }} /> : null }}>
            <MenuItem value="">Overall balance</MenuItem>
            {invoices.filter((i) => i.pendingAmount > 0.005).map((i) => (
              <MenuItem key={i._id} value={i._id}>{i.invoiceNumber || 'Invoice'} · {fmtDate(i.invoiceDate)} · pending {inr(i.pendingAmount)}</MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <TextField select size="small" label="Payment method" value={form.paymentMethod} onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}>
              <MenuItem value="bank_transfer">Bank transfer</MenuItem><MenuItem value="upi">UPI</MenuItem><MenuItem value="cheque">Cheque</MenuItem><MenuItem value="cash">Cash</MenuItem><MenuItem value="other">Other</MenuItem>
            </TextField>
            <TextField size="small" type="number" label="Amount *" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} error={!!errors.amount} helperText={errors.amount}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment>, endAdornment: <Button size="small" onClick={() => setForm({ ...form, amount: String(limitAmount) })} sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}>Full</Button> }} inputProps={{ min: 0, step: '0.01' }} />
          </Box>
          {form.paymentMethod !== 'cash' && form.paymentMethod !== 'other' && (
            <TextField size="small" label={form.paymentMethod === 'cheque' ? 'Cheque number *' : 'Transaction / UTR reference *'} value={form.referenceNumber} onChange={(e) => setForm({ ...form, referenceNumber: e.target.value })} error={!!errors.referenceNumber} helperText={errors.referenceNumber} inputProps={{ maxLength: 100 }} />
          )}
          <TextField size="small" label="Note (optional)" multiline rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} inputProps={{ maxLength: 500 }} />
          {payError && <Alert severity="error">{payError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setPayOpen(false)} disabled={paying} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={submitPay} variant="contained" disabled={paying} startIcon={paying ? <CircularProgress size={15} color="inherit" /> : <MdPayments />} sx={yellowBtn}>
            {paying ? 'Saving…' : `Record ${form.amount ? inr(form.amount) : 'payment'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* history */}
      <Dialog open={histOpen} onClose={() => setHistOpen(false)} maxWidth="md" fullWidth scroll="paper" PaperProps={dialogPaper}>
        <DialogHeader title="Payment history" sub={history?.supplierName || supplier?.supplierName} accent="#8b5cf6" onClose={() => setHistOpen(false)} />
        <DialogContent sx={{ p: 0 }}>
          {histLoading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box> : !(history?.data || []).length ? (
            <Typography sx={{ fontFamily: FONT, color: '#64748b', textAlign: 'center', py: 5 }}>No ledger entries yet.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={head}>Date</TableCell><TableCell sx={head}>Type</TableCell><TableCell sx={head}>Invoice</TableCell><TableCell sx={head}>Method</TableCell><TableCell sx={head}>Reference</TableCell><TableCell sx={head}>By</TableCell><TableCell sx={head} align="right">Amount</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {history.data.map((h) => {
                    const isPay = h.type === 'payment';
                    return (
                      <TableRow key={h._id} hover>
                        <TableCell sx={cell}>{dt(h.createdAt)}</TableCell>
                        <TableCell><Chip size="small" label={isPay ? 'Payment' : 'Invoice'} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: isPay ? '#dcfce7' : '#fef3c7', color: isPay ? '#166534' : '#92400e' }} /></TableCell>
                        <TableCell sx={cell}>{h.invoiceNumber || '—'}</TableCell>
                        <TableCell sx={{ ...cell, textTransform: 'capitalize' }}>{isPay ? methodLabel(h.paymentMethod) : '—'}</TableCell>
                        <TableCell sx={{ ...cell, color: '#64748b', wordBreak: 'break-all' }}>{h.referenceNumber || '—'}{h.note && h.note !== 'Supplier payment' && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>{h.note}</Typography>}</TableCell>
                        <TableCell sx={cell}>{h.paidByName || '—'}</TableCell>
                        <TableCell sx={{ ...cell, fontWeight: 800, color: isPay ? '#166534' : '#b45309' }} align="right">{isPay ? `− ${inr(h.credit)}` : `+ ${inr(h.debit)}`}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setHistOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* invoices */}
      <Dialog open={invOpen} onClose={() => setInvOpen(false)} maxWidth="md" fullWidth scroll="paper" PaperProps={dialogPaper}>
        <DialogHeader title="Invoices" sub={supplier?.supplierName} accent="#f59e0b" onClose={() => setInvOpen(false)} />
        <DialogContent sx={{ p: 0 }}>
          {invLoading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box> : invoices.length === 0 ? (
            <Typography sx={{ fontFamily: FONT, color: '#64748b', textAlign: 'center', py: 5 }}>No invoices received from this supplier.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={head}>Invoice</TableCell><TableCell sx={head}>Date</TableCell><TableCell sx={head} align="right">Amount</TableCell><TableCell sx={head} align="right">Returns</TableCell><TableCell sx={head} align="right">Paid</TableCell><TableCell sx={head} align="right">Pending</TableCell><TableCell sx={head}>Status</TableCell><TableCell sx={head} />
                </TableRow></TableHead>
                <TableBody>
                  {invoices.map((i) => (
                    <TableRow key={i._id} hover>
                      <TableCell sx={{ ...cell, fontWeight: 700 }}>{i.invoiceNumber || '—'}</TableCell>
                      <TableCell sx={cell}>{fmtDate(i.invoiceDate)}</TableCell>
                      <TableCell sx={cell} align="right">{inr(i.originalInvoiceAmount)}</TableCell>
                      <TableCell sx={{ ...cell, color: i.returnAmount ? '#dc2626' : '#94a3b8' }} align="right">{inr(i.returnAmount)}</TableCell>
                      <TableCell sx={{ ...cell, color: '#166534' }} align="right">{inr(i.paidAmount)}</TableCell>
                      <TableCell sx={{ ...cell, fontWeight: 800, color: i.pendingAmount > 0.005 ? '#b45309' : '#94a3b8' }} align="right">{inr(i.pendingAmount)}</TableCell>
                      <TableCell><PayChip pending={i.pendingAmount} paid={i.paidAmount} /></TableCell>
                      <TableCell>{i.pendingAmount > 0.005 && <Button size="small" onClick={() => openPay(supplier, i)} sx={{ textTransform: 'none', fontWeight: 800, fontSize: 12 }}>Pay</Button>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setInvOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
          {supplier?.totalPendingAmount > 0.005 && <Button onClick={() => openPay(supplier)} startIcon={<MdReceiptLong />} variant="contained" sx={yellowBtn}>Pay {inr(supplier.totalPendingAmount)}</Button>}
        </DialogActions>
      </Dialog>

      {/* bank (read only) */}
      <Dialog open={bankOpen} onClose={() => setBankOpen(false)} maxWidth="xs" fullWidth PaperProps={dialogPaper}>
        <DialogHeader title="Bank details" sub={supplier?.supplierName} accent="#3b82f6" onClose={() => setBankOpen(false)} />
        <DialogContent sx={{ p: 3 }}>
          {b?.linked || b?.upiId || b?.accountHolder ? (
            <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', p: 2 }}>
              <Line label="Preferred mode" value={b.preferredMode} />
              <Line label="Account holder" value={b.accountHolder} />
              <Line label="Account number" value={b.accountMasked} />
              <Line label="IFSC" value={b.ifsc} />
              <Line label="SWIFT" value={b.swift} />
              <Line label="UPI ID" value={b.upiId} />
            </Box>
          ) : <Alert severity="warning">No bank details are on file for this supplier.</Alert>}
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#94a3b8', mt: 1.5 }}>Bank details are edited in Supplier management. The full account number is never shown here.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setBankOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SupplierPayout;
